import { Injectable } from '@angular/core'
import { BehaviorSubject } from 'rxjs'
import { BrandColorDefinition, ColorRole, brandColor } from '../api/models/configuration.model'
import { TokenUse } from './brand-token-inspector.service'

export interface SelectedProperty {
    definition: BrandColorDefinition
    /** CSS properties this colour drives, so the row can say what it affects. */
    properties: string[]
}

export interface Selection {
    /** Short descriptor of the element, e.g. "ion-button.register". */
    label: string
    /** Editable colours of the element, grouped by what they paint. */
    byRole: { role: ColorRole, items: SelectedProperty[] }[]
}

const ROLE_ORDER: ColorRole[] = ['background', 'foreground', 'border']

/**
 * What the pick mode is doing, shared between the overlay that follows the pointer and the
 * inspector in the side panel that edits whatever the overlay selected.
 */
@Injectable({ providedIn: 'root' })
export class BrandSelectionService {

    readonly picking = new BehaviorSubject<boolean>(false)
    readonly selection = new BehaviorSubject<Selection>(null)

    startPicking(): void {
        this.picking.next(true)
    }

    stopPicking(): void {
        this.picking.next(false)
    }

    togglePicking(): void {
        this.picking.next(!this.picking.value)
    }

    select(element: Element, tokens: TokenUse[]): void {
        this.selection.next({ label: describe(element), byRole: groupByRole(tokens) })
        this.stopPicking()
    }

    clear(): void {
        this.selection.next(null)
    }
}

function describe(element: Element): string {
    const classes = typeof element.className === 'string'
        ? element.className.trim().split(/\s+/).filter(c => !/^(md|ios|hydrated|ng-)/.test(c))
        : []

    return element.tagName.toLowerCase() + (classes.length ? `.${classes[0]}` : '')
}

function groupByRole(tokens: TokenUse[]): { role: ColorRole, items: SelectedProperty[] }[] {
    const groups = ROLE_ORDER.map(role => ({ role, items: [] as SelectedProperty[] }))

    for (const token of tokens) {
        const definition = brandColor(token.token)
        if (!definition) continue

        const group = groups.find(g => g.role === (definition.role ?? 'background'))
        group.items.push({ definition, properties: token.properties })
    }

    return groups.filter(group => group.items.length > 0)
}
